import { AlertTriangle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@workspace/ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@workspace/ui/components/card';

function isChunkLoadError(error: Error): boolean {
    return error.message.includes('dynamically imported module')
        || error.message.includes('Failed to fetch dynamically imported module')
        || error.message.includes('Loading chunk')
        || error.message.includes('Loading CSS chunk');
}

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        if (isChunkLoadError(error)) {
            const key = 'chunk-error-reload';
            const lastReload = sessionStorage.getItem(key);
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload) > 10_000) {
                sessionStorage.setItem(key, String(now));
                window.location.reload();
            }
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center p-4">
                    <Card className="max-w-md w-full">
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="h-6 w-6 text-destructive" />
                                <CardTitle>Une erreur est survenue</CardTitle>
                            </div>
                            <CardDescription>L&apos;application a rencontré un problème inattendu.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {this.state.error && (
                                <div className="bg-muted p-3 rounded-md">
                                    <p className="text-sm font-mono text-muted-foreground">{this.state.error.message}</p>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <Button onClick={this.handleReset}>Réessayer</Button>
                                <Button onClick={() => (window.location.href = '/')} variant="outline">Retour à l&apos;accueil</Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            );
        }
        return this.props.children;
    }
}
